import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import {
  IssueStatusName,
  issueStatusNameToDisplayName,
} from '@helios/ag-grid-config'
import {
  RootState,
  selectFocusedAccountsAndTotalBreaks,
} from '@helios/state-management'
import {
  fromStringListToNumberArray,
  fromStringToStringList,
} from '@helios/utils'
import {
  IdOrListOfIdsValidationMessages,
  IdOrListValidator,
  NumberOrListValidator,
  StringOrListValidator,
} from '@helios/validators'
import { Store } from '@ngrx/store'
import { ErrorMessage } from '@northfork/ng-basics'
import _ from 'lodash-es'
import { combineLatest, Observable, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  takeUntil,
} from 'rxjs/operators'
import {
  BreakSetValues,
  BreakType,
  Category,
  FindBreaksForm,
} from '../../model/break.model'
import { MultiBreaksService } from '../../services/multi-breaks.service'
import { issuePriorityToDisplayName } from '../../utils/break-priority-column'

@Component({
  selector: 'hs-find-multi-breaks-popover',
  templateUrl: './find-multi-breaks-popover.component.html',
  styleUrls: ['./find-multi-breaks-popover.component.scss'],
})
export class FindMultiBreaksPopoverComponent implements OnInit {
  applyDisabled = true
  errors: ErrorMessage[] =
    IdOrListOfIdsValidationMessages.idOrListOfIdsValidationMessages
  @Output() find = new EventEmitter<FindBreaksForm>()
  @Output() popoverFilterChanged = new EventEmitter<FindBreaksForm>()
  @Input() set accountsFilter(accounts: string[] | null) {
    if (accounts == null) return
    this.rootIdsFilterControl.setValue(accounts.join(','))
  }

  private readonly _destroyed$ = new Subject<void>()

  isFormSubmitting = false

  readonly rootIdType = new FormControl('accountIds', [Validators.required])
  readonly rootIdsFilterControl = new FormControl('', [IdOrListValidator()])
  readonly searchIdType = new FormControl('assetIds')
  readonly searchByIdFilterControl = new FormControl('', [
    StringOrListValidator,
  ])
  readonly breakTypesFilterControl = new FormControl([])
  readonly breakStatusFilterControl = new FormControl([
    issueStatusNameToDisplayName(IssueStatusName.FOUND),
  ])
  readonly breakPriorityFilterControl = new FormControl('')
  readonly breakCategoriesFilterControl = new FormControl([])

  readonly rootIds$: Observable<string> =
    this.rootIdsFilterControl.valueChanges.pipe(
      startWith(''),
      map(x => x as string),
    )

  readonly searchById$: Observable<string> =
    this.searchByIdFilterControl.valueChanges.pipe(
      startWith(''),
      map(x => x as string),
    )

  selectedAccounts$ = this.store.select(selectFocusedAccountsAndTotalBreaks)

  form = new FormGroup({
    rootIdType: this.rootIdType,
    rootIds: this.rootIdsFilterControl,
    searchIdType: this.searchIdType,
    searchIds: this.searchByIdFilterControl,
    breakStatus: this.breakStatusFilterControl,
    breakPriority: this.breakPriorityFilterControl,
    breakType: this.breakTypesFilterControl,
    breakCategories: this.breakCategoriesFilterControl,
  })

  setValues: BreakSetValues = {
    breakType: [],
    priority: [],
    status: [],
    categories: [],
  }

  areInputsEmpty$: Observable<boolean> = combineLatest([
    this.rootIds$,
    this.searchById$,
  ]).pipe(map(values => values.every(s => s.trim().length === 0)))

  statusTypes: string[] = []
  priorityTypes: string[] = []
  breakType: BreakType[] = []
  breakCategories: Category[] = []

  constructor(
    private readonly store: Store<RootState>,
    private readonly breaksService: MultiBreaksService,
  ) {
    this.searchIdType.valueChanges.subscribe(value => {
      if (value === 'securityIds') {
        this.searchByIdFilterControl.addValidators(NumberOrListValidator)
      } else if (value === 'assetIds') {
        this.searchByIdFilterControl.removeValidators(NumberOrListValidator)
      }
      this.searchByIdFilterControl.updateValueAndValidity()
    })
  }

  async ngOnInit() {
    this.selectedAccounts$
      .pipe(
        debounceTime(500),
        takeUntil(this._destroyed$),
        distinctUntilChanged((a, b) => _.isEqual(a, b)),
      )
      .subscribe(selectedAccounts => {
        this.updateAccountsFilter(
          selectedAccounts.accountIds.map(a => `${a}`),
          selectedAccounts.totalBreaks,
        )
      })

    this.form.valueChanges.subscribe(value => {
      this.applyDisabled = !value.rootIds && !value.searchIds
    })
    this.applyDisabled =
      !this.rootIdsFilterControl.value && !this.searchByIdFilterControl.value
    this.breaksService.getSetValues().subscribe(setValues => {
      this.setValues = setValues
      this.statusTypes = setValues.status.map(x =>
        issueStatusNameToDisplayName(x),
      )
      this.priorityTypes = setValues.priority.map(
        x => issuePriorityToDisplayName(x) ?? '',
      )
      this.breakType = setValues.breakType
      this.selectAllForDropdownItems(this.breakType)
      this.breakCategories = setValues.categories
      this.selectAllForDropdownItems(this.breakCategories)
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAllForDropdownItems(items: any[]) {
    items.forEach(element => {
      element['selectedAllGroup'] = 'selectedAllGroup'
    })
  }

  setInitialFilters(initialSearchFilters: FindBreaksForm) {
    this.rootIdType.setValue(initialSearchFilters.rootType)
    this.searchIdType.setValue(initialSearchFilters.searchIdType)
    this.breakTypesFilterControl.setValue(initialSearchFilters.breakTypes)
    this.breakStatusFilterControl.setValue(initialSearchFilters.breakStatus)
    this.breakPriorityFilterControl.setValue(initialSearchFilters.breakPriority)
    this.breakCategoriesFilterControl.setValue(
      initialSearchFilters.breakCategories,
    )
  }

  onSubmit(onApplyOrReset = false) {
    if (onApplyOrReset) {
      this.popoverFilterChanged.emit(this.getCurrentPopOverConfig())
    }
    this.find.next(this.getCurrentPopOverConfig())
  }

  updateAccountsFilter(selectedAccounts: string[], totalKnownBreaks = 0) {
    // Break early
    if (selectedAccounts.length === 0) {
      return
    }

    this.rootIdsFilterControl.setValue(selectedAccounts.join(','))

    if (totalKnownBreaks <= 10000) {
      this.onSubmit()
    }
  }

  getCurrentPopOverConfig(): FindBreaksForm {
    const statusTypeNames = this.breakStatusFilterControl.value as string[]
    const statusTypes = this.setValues.status.filter(x =>
      statusTypeNames.includes(issueStatusNameToDisplayName(x)),
    )
    const priorityTypeNames = this.breakPriorityFilterControl.value as string[]
    const priorityTypes = this.setValues.priority.filter(x =>
      priorityTypeNames.includes(issuePriorityToDisplayName(x) ?? ''),
    )
    const setBreakTypes = this.breakTypesFilterControl.value.map(
      (x: BreakType) => x.displayName,
    ) as string[]
    const breakTypesValue = this.setValues.breakType.filter(x =>
      setBreakTypes.includes(x.displayName),
    )

    const setCategoryType = this.breakCategoriesFilterControl.value.map(
      (x: Category) => x.name,
    ) as string[]
    const categoryType = this.setValues.categories
      .filter(x => setCategoryType.includes(x.name))
      .map(cat => cat.category)
    return {
      rootType: this.rootIdType.value,
      rootIds: fromStringListToNumberArray(
        '' + this.rootIdsFilterControl.value,
      ),
      searchIdType: this.searchIdType.value,
      searchByIds:
        this.searchIdType.value === 'securityIds'
          ? fromStringListToNumberArray('' + this.searchByIdFilterControl.value)
          : fromStringToStringList('' + this.searchByIdFilterControl.value),
      breakStatus: statusTypes as unknown as string[],
      breakPriority: priorityTypes as unknown as string[],
      breakTypes: breakTypesValue as unknown as string[],
      breakIds: [],
      breakCategories: categoryType as unknown as string[],
    }
  }

  resetForm() {
    this.form.setValue(
      {
        rootIdType: 'accountIds',
        rootIds: this.rootIdsFilterControl.value,
        searchIdType: 'assetIds',
        searchIds: '',
        breakStatus: [issueStatusNameToDisplayName(IssueStatusName.FOUND)],
        breakPriority: '',
        breakType: [],
        breakCategories: [],
      },
      { onlySelf: true, emitEvent: false },
    )
    this.onSubmit(true)
  }
}
